import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Layout from '../components/Layout';
import { 
  Plus, 
  Search,
  Package,
  AlertTriangle,
  Calendar,
  TrendingDown,
  TrendingUp,
  Edit,
  Trash2,
  Filter,
  DollarSign,
  BarChart3,
  FileText,
  AlertCircle,
  CheckCircle,
  Clock,
  Building2,
  ArrowUpDown
} from 'lucide-react';
import axios from 'axios';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const InventoryPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const permissions = useMemo(() => new Set(user?.permissions || []), [user?.permissions]);
  const can = (permission) => permissions.has(permission);
  
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({});
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showMovementForm, setShowMovementForm] = useState(false);
  const [showSupplierForm, setShowSupplierForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeTab, setActiveTab] = useState('items');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    brand: '',
    model: '',
    unit: 'unidade',
    current_quantity: 0,
    minimum_quantity: 0,
    maximum_quantity: '',
    unit_cost: '',
    sale_price: '',
    supplier_id: '',
    supplier_name: '',
    expiry_date: '',
    batch_number: '',
    location: '',
    barcode: '',
    alerts_enabled: true
  });

  const [movementData, setMovementData] = useState({
    item_id: '',
    movement_type: 'entrada',
    quantity: 0,
    unit_cost: '',
    reason: '',
    description: '',
    reference_document: '',
    patient_id: '',
    patient_name: '',
    treatment_id: ''
  });

  const [supplierData, setSupplierData] = useState({
    name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    cnpj: '',
    notes: ''
  });

  const tabs = [
    { id: 'items', name: 'Estoque', icon: Package },
    { id: 'movements', name: 'Movimentações', icon: ArrowUpDown },
    { id: 'suppliers', name: 'Fornecedores', icon: Building2 },
    { id: 'reports', name: 'Relatórios', icon: BarChart3 }
  ];

  const movementTypes = [
    { value: 'entrada', label: 'Entrada', color: 'text-green-600' },
    { value: 'saida', label: 'Saída', color: 'text-red-600' },
    { value: 'ajuste', label: 'Ajuste', color: 'text-blue-600' },
    { value: 'perda', label: 'Perda', color: 'text-orange-600' },
    { value: 'vencimento', label: 'Vencimento', color: 'text-purple-600' }
  ];

  const reasons = [
    'compra', 'uso_tratamento', 'descarte', 'ajuste_inventario', 'perda_acidental',
    'vencimento', 'devolucao', 'transferencia', 'doacao', 'outros'
  ];

  const units = [
    'unidade', 'caixa', 'frasco', 'tubo', 'bisnaga', 'ml', 'g', 'kg', 'litro', 'metro'
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    await Promise.all([
      fetchItems(),
      fetchSuppliers(),
      fetchCategories(),
      fetchStats(),
      fetchMovements()
    ]);
    setLoading(false);
  };

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API_URL}/inventory/items`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setItems(response.data);
    } catch (error) {
      console.error('Erro ao carregar itens:', error);
      toast?.error('Erro ao carregar itens do estoque');
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API_URL}/inventory/suppliers`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setSuppliers(response.data);
    } catch (error) {
      console.error('Erro ao carregar fornecedores:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/inventory/categories`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/inventory/stats`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const fetchMovements = async () => {
    try {
      const response = await axios.get(`${API_URL}/inventory/movements`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setMovements(response.data);
    } catch (error) {
      console.error('Erro ao carregar movimentações:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const itemData = {
        ...formData,
        current_quantity: parseFloat(formData.current_quantity || 0),
        minimum_quantity: parseFloat(formData.minimum_quantity || 0),
        maximum_quantity: formData.maximum_quantity ? parseFloat(formData.maximum_quantity) : null,
        unit_cost: formData.unit_cost ? parseFloat(formData.unit_cost) : null,
        sale_price: formData.sale_price ? parseFloat(formData.sale_price) : null
      };

      if (editingItem) {
        await axios.put(`${API_URL}/inventory/items/${editingItem.id}`, itemData, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        toast?.success('Item atualizado com sucesso!');
      } else {
        await axios.post(`${API_URL}/inventory/items`, itemData, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        toast?.success('Item criado com sucesso!');
      }
      
      resetForm();
      fetchItems();
      fetchStats();
    } catch (error) {
      console.error('Erro ao salvar item:', error);
      toast?.error('Erro ao salvar item');
    }
  };

  const handleMovementSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const movData = {
        ...movementData,
        quantity: parseFloat(movementData.quantity),
        unit_cost: movementData.unit_cost ? parseFloat(movementData.unit_cost) : null
      };

      await axios.post(`${API_URL}/inventory/movements`, movData, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast?.success('Movimentação registrada com sucesso!');
      resetMovementForm();
      fetchItems();
      fetchMovements();
      fetchStats();
    } catch (error) {
      console.error('Erro ao registrar movimentação:', error);
      toast?.error(error.response?.data?.detail || 'Erro ao registrar movimentação');
    }
  };

  const handleSupplierSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingSupplier) {
        await axios.put(`${API_URL}/inventory/suppliers/${editingSupplier.id}`, supplierData, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
      } else {
        await axios.post(`${API_URL}/inventory/suppliers`, supplierData, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
      }
      
      toast?.success(editingSupplier ? 'Fornecedor atualizado com sucesso!' : 'Fornecedor criado com sucesso!');
      resetSupplierForm();
      fetchSuppliers();
    } catch (error) {
      console.error('Erro ao criar fornecedor:', error);
      toast?.error('Erro ao criar fornecedor');
    }
  };

  const handleEditSupplier = (supplier) => {
    setEditingSupplier(supplier);
    setSupplierData({
      name: supplier.name || '',
      contact_person: supplier.contact_person || '',
      phone: supplier.phone || '',
      email: supplier.email || '',
      address: supplier.address || '',
      cnpj: supplier.cnpj || '',
      notes: supplier.notes || ''
    });
    setShowSupplierForm(true);
  };

  const handleDeleteSupplier = async (supplier) => {
    if (!window.confirm(`Excluir o fornecedor "${supplier.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/inventory/suppliers/${supplier.id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      toast?.success('Fornecedor excluído com sucesso!');
      fetchSuppliers();
    } catch (error) {
      console.error('Erro ao excluir fornecedor:', error);
      toast?.error(error?.response?.data?.detail || 'Erro ao excluir fornecedor');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description || '',
      category: item.category,
      brand: item.brand || '',
      model: item.model || '',
      unit: item.unit,
      current_quantity: item.current_quantity,
      minimum_quantity: item.minimum_quantity,
      maximum_quantity: item.maximum_quantity || '',
      unit_cost: item.unit_cost || '',
      sale_price: item.sale_price || '',
      supplier_id: item.supplier_id || '',
      supplier_name: item.supplier_name || '',
      expiry_date: item.expiry_date || '',
      batch_number: item.batch_number || '',
      location: item.location || '',
      barcode: item.barcode || '',
      alerts_enabled: item.alerts_enabled
    });
    setShowForm(true);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Tem certeza que deseja excluir este item?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/inventory/items/${itemId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      toast?.success('Item excluído com sucesso!');
      fetchItems();
      fetchStats();
    } catch (error) {
      console.error('Erro ao excluir item:', error);
      toast?.error('Erro ao excluir item');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: '',
      brand: '',
      model: '',
      unit: 'unidade',
      current_quantity: 0,
      minimum_quantity: 0,
      maximum_quantity: '',
      unit_cost: '',
      sale_price: '',
      supplier_id: '',
      supplier_name: '',
      expiry_date: '',
      batch_number: '',
      location: '',
      barcode: '',
      alerts_enabled: true
    });
    setEditingItem(null);
    setShowForm(false);
  };

  const resetMovementForm = () => {
    setMovementData({
      item_id: '',
      movement_type: 'entrada',
      quantity: 0,
      unit_cost: '',
      reason: '',
      description: '',
      reference_document: '',
      patient_id: '',
      patient_name: '',
      treatment_id: ''
    });
    setShowMovementForm(false);
    setSelectedItem(null);
  };

  const resetSupplierForm = () => {
    setSupplierData({
      name: '',
      contact_person: '',
      phone: '',
      email: '',
      address: '',
      cnpj: '',
      notes: ''
    });
    setEditingSupplier(null);
    setShowSupplierForm(false);
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = searchTerm === '' || 
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.brand?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = categoryFilter === '' || item.category === categoryFilter;

    let matchesStatus = true;
    if (statusFilter === 'low_stock') {
      matchesStatus = item.current_quantity <= item.minimum_quantity;
    } else if (statusFilter === 'out_of_stock') {
      matchesStatus = item.current_quantity === 0;
    } else if (statusFilter === 'expiring') {
      const today = new Date();
      const expiryDate = new Date(item.expiry_date);
      const thirtyDaysFromNow = new Date(today.getTime() + (30 * 24 * 60 * 60 * 1000));
      matchesStatus = item.expiry_date && expiryDate <= thirtyDaysFromNow;
    }

    return matchesSearch && matchesCategory && matchesStatus;
  });

  const getStockStatus = (item) => {
    if (item.current_quantity === 0) {
      return { status: 'Sem estoque', color: 'text-red-600', bg: 'bg-red-100' };
    } else if (item.current_quantity <= item.minimum_quantity) {
      return { status: 'Estoque baixo', color: 'text-orange-600', bg: 'bg-orange-100' };
    } else {
      return { status: 'Normal', color: 'text-green-600', bg: 'bg-green-100' };
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Controle de Estoque</h2>
              <p className="text-gray-600">
                Gerencie produtos, movimentações e fornecedores
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {can('inventory.manage') && <button
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center"
              >
                <Plus className="w-4 h-4 mr-2" />
                Novo Item
              </button>}
              {can('inventory.manage') && <button
                onClick={() => setShowMovementForm(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
              >
                <ArrowUpDown className="w-4 h-4 mr-2" />
                Nova Movimentação
              </button>}
              {can('inventory.manage') && <button
                onClick={() => setShowSupplierForm(true)}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center"
              >
                <Building2 className="w-4 h-4 mr-2" />
                Novo Fornecedor
              </button>}
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          <div className="bg-white p-6 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total de Itens</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_items || 0}</p>
              </div>
              <Package className="w-8 h-8 text-purple-600" />
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Estoque Baixo</p>
                <p className="text-2xl font-bold text-orange-600">{stats.low_stock_items || 0}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-orange-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Próximos ao Vencimento</p>
                <p className="text-2xl font-bold text-red-600">{stats.expiring_items || 0}</p>
              </div>
              <Calendar className="w-8 h-8 text-red-600" />
            </div>
          </div>

          {can('inventory.view_costs') && <div className="bg-white p-6 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Valor Total</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(stats.total_inventory_value)}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-green-600" />
            </div>
          </div>}

          <div className="bg-white p-6 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Movimentações (7d)</p>
                <p className="text-2xl font-bold text-blue-600">{stats.recent_movements || 0}</p>
              </div>
              <ArrowUpDown className="w-8 h-8 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? 'border-purple-500 text-purple-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'items' && (
              <div className="space-y-6">
                {/* Filters */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Buscar itens..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Todas as categorias</option>
                    {categories.map(category => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>

                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Todos os status</option>
                    <option value="low_stock">Estoque baixo</option>
                    <option value="out_of_stock">Sem estoque</option>
                    <option value="expiring">Próximo ao vencimento</option>
                  </select>

                  <div className="flex items-center space-x-2">
                    <Filter className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600">
                      {filteredItems.length} de {items.length}
                    </span>
                  </div>
                </div>

                {/* Items List */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Item
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Categoria
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Estoque
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        {can('inventory.view_costs') && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Valor
                        </th>}
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredItems.map((item) => {
                        const stockStatus = getStockStatus(item);
                        const category = categories.find(c => c.value === item.category);
                        return (
                          <tr key={item.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                              <div>
                                <div className="font-medium text-gray-900">{item.name}</div>
                                {item.description && (
                                  <div className="text-sm text-gray-500">{item.description}</div>
                                )}
                                {item.brand && (
                                  <div className="text-xs text-gray-400">
                                    {item.brand} {item.model && `- ${item.model}`}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm text-gray-900">
                                {category?.label || item.category}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm">
                                <div className="font-medium text-gray-900">
                                  {item.current_quantity} {item.unit}
                                </div>
                                <div className="text-xs text-gray-500">
                                  Mín: {item.minimum_quantity} {item.unit}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${stockStatus.bg} ${stockStatus.color}`}>
                                {stockStatus.status}
                              </span>
                            </td>
                            {can('inventory.view_costs') && <td className="px-6 py-4">
                              <div className="text-sm">
                                {item.unit_cost && (
                                  <div className="text-gray-900">
                                    Custo: {formatCurrency(item.unit_cost)}
                                  </div>
                                )}
                                {item.sale_price && (
                                  <div className="text-green-600">
                                    Venda: {formatCurrency(item.sale_price)}
                                  </div>
                                )}
                              </div>
                            </td>}
                            <td className="px-6 py-4 text-right">
                              {can('inventory.manage') && <div className="flex items-center justify-end space-x-2">
                                <button
                                  onClick={() => handleEdit(item)}
                                  className="text-blue-600 hover:text-blue-700 p-1"
                                  title="Editar"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedItem(item);
                                    setMovementData({
                                      ...movementData,
                                      item_id: item.id
                                    });
                                    setShowMovementForm(true);
                                  }}
                                  className="text-green-600 hover:text-green-700 p-1" 
                                  title="Nova Movimentação"
                                >
                                  <ArrowUpDown className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(item.id)}
                                  className="text-red-600 hover:text-red-700 p-1"
                                  title="Excluir"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'movements' && (
              <div className="space-y-6">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Data/Hora
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Item
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tipo
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Quantidade
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Realizado por
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {movements.slice(0, 50).map((movement) => {
                        const movType = movementTypes.find(t => t.value === movement.movement_type);
                        return (
                          <tr key={movement.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 text-sm text-gray-900">
                              {new Date(movement.created_at).toLocaleString('pt-BR')}
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-gray-900">
                                {movement.item_name}
                              </div>
                              {movement.description && (
                                <div className="text-xs text-gray-500">
                                  {movement.description}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <span className={`text-sm font-medium ${movType?.color || 'text-gray-600'}`}>
                                {movType?.label || movement.movement_type}
                              </span>
                              <div className="text-xs text-gray-500">
                                {movement.reason}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-gray-900">
                                {movement.movement_type === 'entrada' ? '+' : '-'}{movement.quantity}
                              </div>
                              {can('inventory.view_costs') && movement.total_cost && (
                                <div className="text-xs text-gray-500">
                                  {formatCurrency(movement.total_cost)}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {movement.performed_by}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'suppliers' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {suppliers.map((supplier) => (
                    <div key={supplier.id} className="bg-gray-50 rounded-lg p-6">
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {supplier.name}
                        </h3>
                        {can('inventory.manage') && <div className="flex space-x-2">
                          <button onClick={() => handleEditSupplier(supplier)} className="text-blue-600 hover:text-blue-700" title="Editar fornecedor">
                            <Edit className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDeleteSupplier(supplier)} className="text-red-600 hover:text-red-700" title="Excluir fornecedor">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>}
                      </div>
                      
                      {supplier.contact_person && (
                        <p className="text-sm text-gray-600 mb-2">
                          Contato: {supplier.contact_person}
                        </p>
                      )}
                      
                      {supplier.phone && (
                        <p className="text-sm text-gray-600 mb-2">
                          Telefone: {supplier.phone}
                        </p>
                      )}
                      
                      {supplier.email && (
                        <p className="text-sm text-gray-600 mb-2">
                          Email: {supplier.email}
                        </p>
                      )}
                      
                      {supplier.cnpj && (
                        <p className="text-sm text-gray-600">
                          CNPJ: {supplier.cnpj}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'reports' && (
              <div className="space-y-6">
                <div className="text-center py-12">
                  <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Relatórios de Estoque
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Funcionalidade em desenvolvimento
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Item Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingItem ? 'Editar Item' : 'Novo Item'}
                </h3>
              </div>
              
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome do Item *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Categoria *
                    </label>
                    <select
                      required
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Selecione uma categoria</option>
                      {categories.map(category => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Marca
                    </label>
                    <input
                      type="text"
                      value={formData.brand}
                      onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Modelo
                    </label>
                    <input
                      type="text"
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Unidade
                    </label>
                    <select
                      value={formData.unit}
                      onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    >
                      {units.map(unit => (
                        <option key={unit} value={unit}>{unit}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quantidade Atual
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.current_quantity}
                      onChange={(e) => setFormData({ ...formData, current_quantity: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Estoque Mínimo
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.minimum_quantity}
                      onChange={(e) => setFormData({ ...formData, minimum_quantity: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Estoque Máximo
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.maximum_quantity}
                      onChange={(e) => setFormData({ ...formData, maximum_quantity: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>

                {can('inventory.view_costs') && <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Custo Unitário (R$)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.unit_cost}
                      onChange={(e) => setFormData({ ...formData, unit_cost: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Preço de Venda (R$)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.sale_price}
                      onChange={(e) => setFormData({ ...formData, sale_price: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fornecedor
                    </label>
                    <select
                      value={formData.supplier_id}
                      onChange={(e) => {
                        const supplier = suppliers.find(s => s.id === e.target.value);
                        setFormData({ 
                          ...formData, 
                          supplier_id: e.target.value,
                          supplier_name: supplier ? supplier.name : ''
                        });
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Selecione um fornecedor</option>
                      {suppliers.map(supplier => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data de Validade
                    </label>
                    <input
                      type="date"
                      value={formData.expiry_date}
                      onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Lote
                    </label>
                    <input
                      type="text"
                      value={formData.batch_number}
                      onChange={(e) => setFormData({ ...formData, batch_number: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Localização
                    </label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                      placeholder="Ex: Prateleira A1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Código de Barras
                    </label>
                    <input
                      type="text"
                      value={formData.barcode}
                      onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.alerts_enabled}
                      onChange={(e) => setFormData({ ...formData, alerts_enabled: e.target.checked })}
                      className="mr-2 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">
                      Habilitar alertas de estoque baixo
                    </span>
                  </label>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    {editingItem ? 'Atualizar' : 'Criar'} Item
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Movement Form Modal */}
        {showMovementForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-lg w-full">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Nova Movimentação
                  {selectedItem && (
                    <span className="text-sm font-normal text-gray-600 block">
                      {selectedItem.name}
                    </span>
                  )}
                </h3>
              </div>
              
              <form onSubmit={handleMovementSubmit} className="p-6 space-y-4">
                {!selectedItem && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Item *
                    </label>
                    <select
                      required
                      value={movementData.item_id}
                      onChange={(e) => setMovementData({ ...movementData, item_id: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Selecione um item</option>
                      {items.map(item => (
                        <option key={item.id} value={item.id}>
                          {item.name} - {item.current_quantity} {item.unit}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo de Movimentação *
                    </label>
                    <select
                      required
                      value={movementData.movement_type}
                      onChange={(e) => setMovementData({ ...movementData, movement_type: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    >
                      {movementTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quantidade *
                    </label>
                    <input
                      type="number"
                      required
                      min="0.01"
                      step="0.01"
                      value={movementData.quantity}
                      onChange={(e) => setMovementData({ ...movementData, quantity: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Motivo *
                    </label>
                    <select
                      required
                      value={movementData.reason}
                      onChange={(e) => setMovementData({ ...movementData, reason: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Selecione um motivo</option>
                      {reasons.map(reason => (
                        <option key={reason} value={reason}>
                          {reason.replace('_', ' ').charAt(0).toUpperCase() + reason.slice(1).replace('_', ' ')}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {can('inventory.view_costs') && <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Custo Unitário (R$)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={movementData.unit_cost}
                      onChange={(e) => setMovementData({ ...movementData, unit_cost: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    />
                  </div>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Documento de Referência
                  </label>
                  <input
                    type="text"
                    value={movementData.reference_document}
                    onChange={(e) => setMovementData({ ...movementData, reference_document: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    placeholder="Ex: NF 12345"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Observações
                  </label>
                  <textarea
                    value={movementData.description}
                    onChange={(e) => setMovementData({ ...movementData, description: e.target.value })}
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    placeholder="Observações adicionais..."
                  />
                </div>
                
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={resetMovementForm}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Registrar Movimentação
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Supplier Form Modal */}
        {showSupplierForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-lg w-full">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingSupplier ? 'Editar Fornecedor' : 'Novo Fornecedor'}
                </h3>
              </div>
              
              <form onSubmit={handleSupplierSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome do Fornecedor *
                  </label>
                  <input
                    type="text"
                    required
                    value={supplierData.name}
                    onChange={(e) => setSupplierData({ ...supplierData, name: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Pessoa de Contato
                    </label>
                    <input
                      type="text"
                      value={supplierData.contact_person}
                      onChange={(e) => setSupplierData({ ...supplierData, contact_person: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefone
                    </label>
                    <input
                      type="tel"
                      value={supplierData.phone}
                      onChange={(e) => setSupplierData({ ...supplierData, phone: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      E-mail
                    </label>
                    <input
                      type="email"
                      value={supplierData.email}
                      onChange={(e) => setSupplierData({ ...supplierData, email: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CNPJ
                    </label>
                    <input
                      type="text"
                      value={supplierData.cnpj}
                      onChange={(e) => setSupplierData({ ...supplierData, cnpj: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                      placeholder="00.000.000/0001-00"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Endereço
                  </label>
                  <textarea
                    value={supplierData.address}
                    onChange={(e) => setSupplierData({ ...supplierData, address: e.target.value })}
                    rows={2}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Observações
                  </label>
                  <textarea
                    value={supplierData.notes}
                    onChange={(e) => setSupplierData({ ...supplierData, notes: e.target.value })}
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={resetSupplierForm}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    {editingSupplier ? 'Salvar alterações' : 'Criar Fornecedor'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default InventoryPage;
